import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * MobileReportingLauncher — /app/raportti/:reportId/kentta
 *
 * Purpose:
 *   Opens the same mobile field-worker view that installers see via a share
 *   link (/r-edit/{edit_token}) but from the pääkäyttäjä's authenticated
 *   session. No new report is created, no report data is modified — we just
 *   fetch the current report to look up its edit_token and then navigate the
 *   browser into the existing PublicReportEdit component so the office user
 *   can review or fill in fields the same way the installer would.
 *
 * This satisfies the design goal that there is only ONE mobile reporting
 * UI in the app, accessible via three entry points (link, this button, and
 * later the "Omat työt" list for field-worker accounts).
 */
export default function MobileReportingLauncher() {
  const { reportId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`);
        const token = data?.edit_token;
        if (cancelled) return;
        if (!token) {
          toast.error("Mobiiliraportointia ei voi avata tälle raportille");
          navigate(`/app/raportti/${reportId}`, { replace: true });
          return;
        }
        navigate(`/r-edit/${token}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        toast.error("Raportin lataus epäonnistui");
        navigate(`/app/raportti/${reportId}`, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="mobile-launcher-loading">
      <div className="flex items-center gap-3 text-slate-600">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        <span className="text-sm">Avataan mobiiliraportointia…</span>
      </div>
    </div>
  );
}
